import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";

const PERIODS = [
  { label: "7 days",  value: "7daysAgo"  },
  { label: "30 days", value: "30daysAgo" },
  { label: "90 days", value: "90daysAgo" },
];

const CHANNEL_COLORS = [
  "#7145d0", "#9f7cf5", "#5bb4f0", "#79e08f",
  "#f0a35b", "#f05b7a", "#a8a8a8", "#e0c87a",
];

const fmt     = (n) => Number(n).toLocaleString("en-GB");
const fmtDate = (d) => (!d || d.length !== 8) ? d : `${d.slice(6)}/${d.slice(4, 6)}`;

// ── API calls ─────────────────────────────────────────────────────────────────

const fetchSettings  = () => axios.get("/api/analytics/ga4-settings").then((r) => r.data);
const fetchMarketing = (period) => axios.get("/api/analytics/marketing", { params: { period } }).then((r) => r.data);
const saveSettings   = (data) => axios.put("/api/analytics/ga4-settings", data).then((r) => r.data);

// ── Sub-components ────────────────────────────────────────────────────────────

const LineTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#1e1e1e", border: "1px solid #303030", borderRadius: 10, padding: "10px 14px" }}>
      <div style={{ color: "#595959", fontSize: 11, marginBottom: 4 }}>{fmtDate(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color, fontWeight: 600, fontSize: 13 }}>
          {fmt(p.value)} {p.name}
        </div>
      ))}
    </div>
  );
};

const KpiCard = ({ label, value, sub }) => (
  <div className="ga4-kpi-card">
    <span className="ga4-kpi-card__label">{label}</span>
    <span className="ga4-kpi-card__value">{value}</span>
    {sub && <span className="ga4-kpi-card__sub">{sub}</span>}
  </div>
);

// ── Setup card ────────────────────────────────────────────────────────────────

function SetupCard({ serviceAccountEmail, onConnected }) {
  const [step, setStep]         = useState(1);
  const [propertyId, setPropertyId] = useState("");
  const [error, setError]       = useState("");
  const queryClient             = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => saveSettings({ property_id: propertyId.trim() }),
    onSuccess: (data) => {
      queryClient.setQueryData(["ga4-settings"], data);
      onConnected();
    },
    onError: (err) => {
      setError(err.response?.data?.error || "Connection failed. Check the property ID and try again.");
    },
  });

  const copyEmail = () => navigator.clipboard.writeText(serviceAccountEmail);

  return (
    <div className="ga4-setup">
      <div className="ga4-setup__icon">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
        </svg>
      </div>
      <h3 className="ga4-setup__title">Connect Google Analytics</h3>
      <p className="ga4-setup__sub">Two quick steps to bring your website traffic into this dashboard.</p>

      <div className="ga4-setup__steps">
        {/* Step 1 */}
        <div className={`ga4-setup__step${step >= 1 ? " ga4-setup__step--active" : ""}`}>
          <div className="ga4-setup__step-num">1</div>
          <div className="ga4-setup__step-body">
            <div className="ga4-setup__step-title">Add our service account to your GA4 property</div>
            <p className="ga4-setup__step-desc">
              In Google Analytics → Admin → Property Access Management → Add users, paste this email and set the role to <strong>Viewer</strong>.
            </p>
            <div className="ga4-setup__email-row">
              <code className="ga4-setup__email">{serviceAccountEmail}</code>
              <button className="ga4-setup__copy-btn" onClick={copyEmail} title="Copy">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
              </button>
            </div>
            {step === 1 && (
              <button className="ga4-setup__next-btn" onClick={() => setStep(2)}>
                Done, next step →
              </button>
            )}
          </div>
        </div>

        {/* Step 2 */}
        <div className={`ga4-setup__step${step >= 2 ? " ga4-setup__step--active" : ""}`}>
          <div className="ga4-setup__step-num">2</div>
          <div className="ga4-setup__step-body">
            <div className="ga4-setup__step-title">Enter your GA4 Property ID</div>
            <p className="ga4-setup__step-desc">
              Find it in GA4 → Admin → Property Settings. It's a number like <code>356457239</code>.
            </p>
            {step >= 2 && (
              <>
                <div className="ga4-setup__input-row">
                  <input
                    className="ga4-setup__input"
                    type="text"
                    placeholder="e.g. 356457239"
                    value={propertyId}
                    onChange={(e) => { setPropertyId(e.target.value); setError(""); }}
                    autoFocus
                  />
                  <button
                    className="ga4-setup__connect-btn"
                    onClick={() => mutation.mutate()}
                    disabled={!propertyId.trim() || mutation.isPending}
                  >
                    {mutation.isPending ? "Connecting…" : "Connect"}
                  </button>
                </div>
                {error && <div className="ga4-setup__error">{error}</div>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

function Dashboard({ onDisconnect }) {
  const [period, setPeriod]     = useState("30daysAgo");
  const [showSettings, setShowSettings] = useState(false);
  const queryClient             = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["ga4-marketing", period],
    queryFn: () => fetchMarketing(period),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const disconnectMutation = useMutation({
    mutationFn: () => saveSettings({ property_id: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ga4-settings"] });
      onDisconnect();
    },
  });

  const sessionsData = (data?.sessions_over_time ?? []).map((r) => ({
    date: r.dimensions[0],
    sessions: Number(r.metrics[0]),
    users:    Number(r.metrics[1]),
  }));

  const channelsData   = (data?.traffic_channels ?? []).map((r) => ({ name: r.dimensions[0], value: Number(r.metrics[0]) }));
  const topPages       = data?.top_pages  ?? [];
  const countries      = data?.countries  ?? [];
  const totalSessions  = sessionsData.reduce((s, r) => s + r.sessions, 0);
  const totalUsers     = sessionsData.reduce((s, r) => s + r.users, 0);
  const totalPageviews = topPages.reduce((s, r) => s + Number(r.metrics[0]), 0);

  return (
    <div className="ga4-view">
      <div className="ga4-view__header">
        <h2 className="ga4-view__title">Website Analytics</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="ga4-period-pills">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                className={`ga4-period-pill${period === p.value ? " ga4-period-pill--active" : ""}`}
                onClick={() => setPeriod(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button className="ga4-settings-btn" onClick={() => setShowSettings((v) => !v)} title="GA4 settings">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="ga4-settings-panel">
          <span className="ga4-settings-panel__label">Google Analytics is connected.</span>
          <button
            className="ga4-settings-panel__disconnect"
            onClick={() => disconnectMutation.mutate()}
            disabled={disconnectMutation.isPending}
          >
            {disconnectMutation.isPending ? "Disconnecting…" : "Disconnect"}
          </button>
        </div>
      )}

      {isError && (
        <div className="ga4-error">Could not load analytics data. Check that the service account still has Viewer access on this GA4 property.</div>
      )}

      {isLoading ? (
        <div className="ga4-loading"><div className="ga4-loading__spinner" /><span>Loading analytics…</span></div>
      ) : (
        <>
          <div className="ga4-kpi-row">
            <KpiCard label="Sessions"   value={fmt(totalSessions)}  sub="total visits" />
            <KpiCard label="Users"      value={fmt(totalUsers)}     sub="active users" />
            <KpiCard label="Page Views" value={fmt(totalPageviews)} sub="across all pages" />
          </div>

          <div className="ga4-charts-row">
            <div className="ga4-widget ga4-widget--wide">
              <div className="ga4-widget__head"><span className="ga4-widget__title">Sessions &amp; Users Over Time</span></div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={sessionsData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="#1f1f1f" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fill: "#595959", fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fill: "#595959", fontSize: 11 }} axisLine={false} tickLine={false} width={36} />
                  <Tooltip content={<LineTip />} />
                  <Line type="monotone" dataKey="sessions" name="sessions" stroke="#7145d0" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="users"    name="users"    stroke="#79e08f" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
              <div className="ga4-legend">
                <span className="ga4-legend__item ga4-legend__item--purple">Sessions</span>
                <span className="ga4-legend__item ga4-legend__item--green">Users</span>
              </div>
            </div>

            <div className="ga4-widget ga4-widget--narrow">
              <div className="ga4-widget__head"><span className="ga4-widget__title">Traffic Channels</span></div>
              {channelsData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={channelsData} cx="50%" cy="50%" innerRadius={52} outerRadius={80} dataKey="value" paddingAngle={2}>
                        {channelsData.map((_, i) => <Cell key={i} fill={CHANNEL_COLORS[i % CHANNEL_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="ga4-channel-list">
                    {channelsData.map((c, i) => (
                      <div key={i} className="ga4-channel-item">
                        <span className="ga4-channel-item__dot" style={{ background: CHANNEL_COLORS[i % CHANNEL_COLORS.length] }} />
                        <span className="ga4-channel-item__name">{c.name}</span>
                        <span className="ga4-channel-item__val">{fmt(c.value)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : <div className="ga4-empty">No channel data</div>}
            </div>
          </div>

          <div className="ga4-tables-row">
            <div className="ga4-widget ga4-widget--half">
              <div className="ga4-widget__head"><span className="ga4-widget__title">Top Pages</span></div>
              <table className="ga4-table">
                <thead><tr><th>Page</th><th className="ga4-table__num">Views</th><th className="ga4-table__num">Users</th></tr></thead>
                <tbody>
                  {topPages.map((r, i) => (
                    <tr key={i}>
                      <td className="ga4-table__path" title={r.dimensions[1]}>{r.dimensions[0]}</td>
                      <td className="ga4-table__num">{fmt(r.metrics[0])}</td>
                      <td className="ga4-table__num">{fmt(r.metrics[1])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="ga4-widget ga4-widget--half">
              <div className="ga4-widget__head"><span className="ga4-widget__title">Users by Country</span></div>
              <table className="ga4-table">
                <thead><tr><th>Country</th><th className="ga4-table__num">Users</th></tr></thead>
                <tbody>
                  {countries.map((r, i) => (
                    <tr key={i}>
                      <td>{r.dimensions[0]}</td>
                      <td className="ga4-table__num">{fmt(r.metrics[0])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function MarketingView() {
  const { data: settings, isLoading } = useQuery({
    queryKey: ["ga4-settings"],
    queryFn:  fetchSettings,
    staleTime: Infinity,
  });

  const [forceSetup, setForceSetup] = useState(false);

  if (isLoading) {
    return <div className="ga4-loading"><div className="ga4-loading__spinner" /><span>Loading…</span></div>;
  }

  const connected = settings?.connected && !forceSetup;

  if (!connected) {
    return (
      <SetupCard
        serviceAccountEmail={settings?.service_account_email ?? ""}
        onConnected={() => setForceSetup(false)}
      />
    );
  }

  return <Dashboard onDisconnect={() => setForceSetup(true)} />;
}
